/**
 * citationMarkerEvents — detect when the writer's cursor lands inside one
 * of our ``asthra-cite:*`` inline content controls and dispatch a citation-
 * fetch click event.
 *
 * # DECISION (2026-06-08): selection-change polling, not ``cc.onEntered``.
 * # WHY: ``ContentControl.onEntered`` does not fire reliably for inline
 * #      content controls in this Word host. Both ``Hidden`` (no event at
 * #      all) and ``BoundingBox`` (visible brackets but still no event)
 * #      were tested. The host's already-wired
 * #      ``subscribeSelectionChange`` (1.5 s debounce, attached to
 * #      ``DocumentSelectionChanged``) does fire — we use it as the
 * #      trigger and ask Office.js for the selection's parent content
 * #      control on each cursor-settle. If the tag starts with
 * #      ``asthra-cite:`` we dispatch.
 *
 * # PUBLIC API
 *  The shell-facing exports (``subscribeCitationMarkerEvents``,
 *  ``refreshCitationMarkerEvents``, ``unsubscribeAllCitationMarkerEvents``)
 *  preserve their previous shapes so VariantCShell and useWordWriteQueue
 *  don't change. ``refresh`` is now a no-op — selection-change is global
 *  so there is nothing to re-subscribe when new content controls appear.
 *
 * # DEBOUNCE
 *  Word fires selection-change on every caret move; the 1.5 s debounce in
 *  ``subscribeSelectionChange`` handles that. We add a smaller 250 ms
 *  per-tag dedup so a quick cursor bounce across a marker's start/end
 *  produces one fetch instead of two.
 */

import { parkWhileWordBusy, queueWordRead } from "../../services/wordReadQueue";
import { subscribeSelectionChange } from "../../services/wordProbe";
import { CITATION_CC_TAG_PREFIX, parseCitationTag } from "./citationMarkerWriter";

/** Payload delivered to the click handler. Carries the section number the
 *  control belongs to (as stored in its tag) and the canonical citation key
 *  (``"1"``, ``"1,2"``, ``"Smith2024"``). ``controlId`` is the Office.js
 *  content-control id; the context-walker re-queries it under a fresh
 *  ``Word.run`` to read the live range. */
export interface CitationMarkerClickEvent {
  sectionNumber: string;
  key: string;
  controlId: number;
}

export type CitationMarkerClickHandler = (event: CitationMarkerClickEvent) => void;

/** Per-tag dedup window — the 1.5 s debounce in ``subscribeSelectionChange``
 *  already coalesces caret-bounce events; this adds a smaller guard against
 *  duplicate fires for the same marker (e.g. a rapid undo/redo that lands
 *  the cursor on the same CC twice in quick succession). */
const DEBOUNCE_MS = 250;
let lastEntered: { tag: string; at: number } | null = null;

/** Active handler reference. Set by ``subscribeCitationMarkerEvents`` and
 *  cleared by ``unsubscribeAllCitationMarkerEvents``. The selection-change
 *  callback no-ops when ``handlerRef`` is null. */
let handlerRef: CitationMarkerClickHandler | null = null;

/** Teardown returned by ``subscribeSelectionChange``. We hold it so that
 *  unsubscribing the citation events also tears down the underlying
 *  Office.js handler (the wordProbe module ref-counts subscribers; once
 *  the last one leaves the Office.js handler detaches). */
let selChangeUnsub: (() => void) | null = null;

function isOfficeReady(): boolean {
  return (
    typeof Word !== "undefined" &&
    typeof Word.run === "function" &&
    typeof Office !== "undefined" &&
    Office.context !== undefined
  );
}

/** Detect whether the current cursor is inside an ``asthra-cite:*`` content
 *  control. Runs inside ``Word.run`` so the Office.js proxies are valid;
 *  the result is dispatched to ``handlerRef`` synchronously after sync.
 *  Serialized with the other passive reads, and parked while a Load or write
 *  owns Word — replayed when it is idle, so a click into a citation during a
 *  write still opens its popover. */
async function detectCursorMarker(): Promise<void> {
  if (!isOfficeReady() || !handlerRef) return;
  if (parkWhileWordBusy("citationMarker", () => void detectCursorMarker())) return;
  try {
    await queueWordRead("citationMarker", () => Word.run(async (context) => {
      const selection = context.document.getSelection();
      // ``parentContentControlOrNullObject`` returns the innermost CC that
      // contains the range; for inline citation CCs this is exactly the
      // wrap we care about. Outer non-citation CCs (e.g. table cells if
      // they ever exist) get filtered by the tag prefix check below.
      const cc = selection.parentContentControlOrNullObject;
      cc.load("tag,id,isNullObject");
      await context.sync();
      if (cc.isNullObject) return;
      if (!cc.tag || !cc.tag.startsWith(CITATION_CC_TAG_PREFIX)) return;
      const parsed = parseCitationTag(cc.tag);
      if (!parsed) return;
      const now = Date.now();
      if (
        lastEntered &&
        lastEntered.tag === cc.tag &&
        now - lastEntered.at < DEBOUNCE_MS
      ) {
        return;
      }
      lastEntered = { tag: cc.tag, at: now };
      // Tracer — when the citation popover is broken end-to-end this is
      // the single most useful line: no log → wrap pass didn't run for
      // this section, or cursor isn't actually inside the CC.
      console.log(`[citationMarkerEvents] cursor entered ${cc.tag}`);
      handlerRef?.({
        sectionNumber: parsed.sectionNumber,
        key: parsed.key,
        controlId: cc.id,
      });
    }));
  } catch (err) {
    console.warn(
      "[citationMarkerEvents] cursor detect failed (non-fatal):",
      err,
    );
  }
}

/** Public: install the click handler and start watching cursor-settle
 *  events. Idempotent — calling twice replaces the handler reference but
 *  does not double-subscribe to selection-change. Returns the teardown
 *  the caller invokes from useEffect cleanup. */
export async function subscribeCitationMarkerEvents(
  handler: CitationMarkerClickHandler,
): Promise<() => Promise<void>> {
  handlerRef = handler;
  if (!selChangeUnsub && isOfficeReady()) {
    selChangeUnsub = subscribeSelectionChange(() => {
      void detectCursorMarker();
    });
  }
  return unsubscribeAllCitationMarkerEvents;
}

/** Public: kept for API shape compatibility with the previous
 *  ``onEntered``-based implementation. Under selection-change polling
 *  there is no per-control subscription to refresh — the watcher
 *  reads ``parentContentControlOrNullObject`` from the live selection
 *  on every settle, so newly-created content controls become clickable
 *  as soon as the writer's cursor lands inside them. The
 *  ``useWordWriteQueue`` post-section pipeline still calls this; we
 *  return 0 to signal "no new subscriptions added" (the historical
 *  return value). */
export async function refreshCitationMarkerEvents(): Promise<number> {
  return 0;
}

/** Public: tear down the selection-change subscription and clear the
 *  handler. Failures during teardown are swallowed — they're harmless
 *  because the wordProbe module also no-ops when Office isn't ready. */
export async function unsubscribeAllCitationMarkerEvents(): Promise<void> {
  handlerRef = null;
  lastEntered = null;
  if (selChangeUnsub) {
    try {
      selChangeUnsub();
    } catch {
      /* non-fatal — wordProbe handles unattach idempotently */
    }
    selChangeUnsub = null;
  }
}
